/**
 * FavouritesWidget Hook - Orchestrates mediator + store
 * Fetches favourites on mount, provides actions for add/remove
 */

import { useEffect, useCallback } from 'react';
import { logger, mediator } from '@/application/core';
import {
  GetFavouritesQuery,
  AddFavouriteCommand,
  RemoveFavouriteCommand,
  type FavouritesResponse,
  type FavouriteItem,
} from '@/application';
import {
  useFavourites,
  useFavouritesLoading,
  useFavouritesError,
  useIsCurrentPageFavourited,
  useSetFavouritesData,
  useSetFavouritesLoading,
  useSetFavouritesError,
} from './FavouritesWidget.store';

export function useFavouritesWidget() {
  const favourites = useFavourites();
  const loading = useFavouritesLoading();
  const error = useFavouritesError();
  const isCurrentPageFavourited = useIsCurrentPageFavourited();
  const setFavourites = useSetFavouritesData();
  const setLoading = useSetFavouritesLoading();
  const setError = useSetFavouritesError();

  const removeUrlFromFavourites = useCallback(
    (items: FavouriteItem[], urlToRemove: string) =>
      items.filter((item) => item.url !== urlToRemove),
    []
  );

  const clearLegacyFavouritesSessionCache = useCallback(() => {
    try {
      window.sessionStorage.removeItem('FavouritePages');
    } catch (err) {
      logger.warn('FavouritesWidget', 'Failed to clear legacy session cache:', err);
    }
  }, []);

  // Fetch favourites on mount
  useEffect(() => {
    let mounted = true;

    async function init() {
      setError(null);
      setLoading(true);
      try {
        const result = await mediator.send<FavouritesResponse>(new GetFavouritesQuery());
        if (mounted) {
          setFavourites(result.favourites);
        }
      } catch (err) {
        logger.error('FavouritesWidget', 'Error fetching favourites:', err);
        if (mounted) {
          setError(err instanceof Error ? err : new Error('Failed to fetch favourites'));
        }
      }
    }

    init();
    return () => {
      mounted = false;
    };
  }, [setFavourites, setLoading, setError]);

  // Add favourite (optimistic update)
  const addFavourite = useCallback(
    async (name: string, url: string) => {
      const previousFavourites = favourites;

      // Optimistic update - use negative timestamp as temporary ID to avoid collision
      const newFavourite = {
        id: -Date.now(), // Temporary client-side ID (negative to distinguish from backend IDs)
        name,
        url,
        icon: null,
      };
      setFavourites([...favourites, newFavourite]);

      try {
        const result = await mediator.send<FavouritesResponse>(new AddFavouriteCommand(name, url));
        setFavourites(result.favourites);
        clearLegacyFavouritesSessionCache();
      } catch (err) {
        logger.error('FavouritesWidget', 'Error adding favourite:', err);
        // Rollback on error
        setFavourites(previousFavourites);
        setError(err instanceof Error ? err : new Error('Failed to add favourite'));
      }
    },
    [favourites, setFavourites, setError, clearLegacyFavouritesSessionCache]
  );

  // Remove favourite (optimistic update)
  const removeFavourite = useCallback(
    async (url: string) => {
      const previousFavourites = favourites;

      // Optimistic update
      setFavourites(removeUrlFromFavourites(favourites, url));

      try {
        const result = await mediator.send<FavouritesResponse>(new RemoveFavouriteCommand(url));

        // Keep removed URL out of local state even if response is stale
        setFavourites(removeUrlFromFavourites(result.favourites, url));
        clearLegacyFavouritesSessionCache();
      } catch (err) {
        logger.error('FavouritesWidget', 'Error removing favourite:', err);
        // Rollback on error
        setFavourites(previousFavourites);
        setError(err instanceof Error ? err : new Error('Failed to remove favourite'));
      }
    },
    [favourites, setFavourites, setError, clearLegacyFavouritesSessionCache, removeUrlFromFavourites]
  );

  // Get current URL (cleaned)
  const currentUrl = window.location.pathname.replace(/#$/, '');

  return {
    favourites,
    loading,
    error,
    isCurrentPageFavourited,
    currentUrl,
    addFavourite,
    removeFavourite,
  };
}
